import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { Field, useDismiss } from "@cntrl/ui";
import styles from "./MicSuggest.module.scss";

/** One catalog entry. `micId` null = a band custom the user typed before, kept as free text. */
export interface MicOption {
  label: string;
  micId: string | null;
}

export interface MicSuggestProps {
  value: string;
  /** A CATALOG pick carries its `micId`; anything typed carries `micId: null` (⇒ `micCustom` on save). */
  onChange: (next: { mic: string; micId: string | null }) => void;
  options: readonly MicOption[];
  label: string;
  placeholder?: string;
  /** Bubbles up whatever this control does NOT consume — the row's Enter-commits / Escape-cancels. */
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  className?: string;
}

/** The specimen caps the popup at eight rows (`.slice(0, 8)`); the catalog is small but a band's own
 *  customs push it past a usable list otherwise. */
const MAX_SUGGESTIONS = 8;

/**
 * The mic field — a free-text input with a filtered suggestion popup.
 *
 * **Why this is not a `<datalist>` and not `<Picker>`.** SME TASK-017, "look at how my drop down
 * looks": the black slab in his screenshot is not our CSS at all, it is the browser's own `<datalist>`
 * chrome rendering in dark mode, and no stylesheet can touch it. `<Picker typeahead>` cannot serve
 * either — it is a SELECT, so it has no free-text commit path (a mic that is not in the global catalog
 * must still be enterable), and on a coarse pointer it degrades to the native `<select>`, which would
 * delete the custom-mic capability on the mobile shell this same screen renders under.
 *
 * So this is feature-local, and deliberately so: it is the second free-text-plus-suggestions control in
 * the app, and the standing decision is to build local and queue the extraction rather than
 * pre-emptively pull a combobox into `@cntrl/ui` off one more consumer (KNOWN-ISSUES §4).
 *
 * The popup PORTALS to `document.body`. It has to: the input-list is now an `overflow-y: auto` scroll
 * container, which clips an absolutely-positioned child — open the popup on the last visible row of a
 * long list and an inline popover would be cut in half.
 */
export function MicSuggest({ value, onChange, options, label, placeholder, onKeyDown: onKeyDownOuter, className }: MicSuggestProps) {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    const pool = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return pool.slice(0, MAX_SUGGESTIONS);
  }, [options, value]);

  const close = () => { setOpen(false); setActive(-1); };
  useDismiss(open, close, rootRef);

  // Fixed-position anchor for the portaled popup, re-measured on any scroll (capture catches the
  // input-list's own scroller) and on resize. Flips upward when the space below is tighter.
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  useEffect(() => {
    if (!open) { setAnchor(null); return; }
    const measure = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const up = spaceBelow < 240 && r.top > spaceBelow;
      setAnchor({ top: up ? r.top - 4 : r.bottom + 4, left: r.left, width: r.width, up });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  const pick = (o: MicOption) => { onChange({ mic: o.label, micId: o.micId }); close(); };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      if (matches.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + matches.length) % matches.length);
      return;
    }
    if (e.key === "Enter" && open && active >= 0 && matches[active]) {
      // Consume the Enter that PICKS; a bare Enter still bubbles to the row, which commits the edit.
      e.preventDefault();
      e.stopPropagation();
      pick(matches[active]!);
      return;
    }
    if (e.key === "Escape" && open) {
      // Escape closes the popup FIRST and only then, on a second press, reaches the row's cancel —
      // one layer at a time, the same contract the overlay stack uses.
      e.stopPropagation();
      close();
      return;
    }
    // Anything this control did not consume belongs to the caller.
    onKeyDownOuter?.(e);
  };

  return (
    <div ref={rootRef} className={clsx(styles.wrap, className)}>
      {/* The BOX is the shared `<Field>` (ART-80) — same 1.5px hairline, `--ac-radius-sm`, `--ac-control-h`
          and accent focus ring as every other text box on the page. Only the combobox behaviour is local.
          `hideLabel` keeps a real <label> for the listbox's name without printing one inside the row pill. */}
      <Field
        ref={inputRef}
        type="text"
        role="combobox"
        label={label}
        hideLabel
        value={value}
        placeholder={placeholder}
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && active >= 0 ? `${baseId}-opt-${active}` : undefined}
        autoComplete="off"
        onChange={(e) => { onChange({ mic: e.target.value, micId: null }); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && anchor && matches.length > 0 && createPortal(
        <ul
          id={listboxId}
          role="listbox"
          aria-label={label}
          className={styles.list}
          style={{
            top: anchor.up ? undefined : anchor.top,
            bottom: anchor.up ? window.innerHeight - anchor.top : undefined,
            left: anchor.left,
            minWidth: Math.max(anchor.width, 180),
          }}
          // The portal is not a DOM child of rootRef, so useDismiss would read a press here as an
          // outside dismiss and close the list before the pick lands. preventDefault also keeps focus
          // on the input, so the blur never repaints the list under the pointer mid-click.
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          {matches.map((o, i) => (
            <li
              key={o.label}
              id={`${baseId}-opt-${i}`}
              role="option"
              // Programmatically focusable only — DOM focus stays on the combobox input
              // (aria-activedescendant is the keyboard model), same as `Picker`'s option.
              tabIndex={-1}
              aria-selected={o.label === value}
              className={clsx(styles.opt, i === active && styles.optActive)}
              onPointerEnter={() => setActive(i)}
              // The commit is `onClick`, NOT `onMouseDown` — `Picker` does the same for the same
              // reason (Picker.tsx). The `<ul>` above cancels `pointerdown` to defeat `useDismiss`,
              // and per the Pointer Events spec a cancelled `pointerdown` suppresses the
              // compatibility `mousedown` entirely while `click` still fires; committing on
              // `mousedown` therefore made every option dead to mouse and touch.
              onClick={() => pick(o)}
              // Ordinarily unreachable (focus lives on the input), but if focus is ever placed here
              // programmatically the option still commits from the keyboard.
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(o); }
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
